import { PcsSizingEngineResponse } from '../models/pcs-sizing-engine-response.model';

/**
 * PCS-2486: this project's OWN computed per-host spec for one recommendation cluster - the same
 * numbers the Hardware Configuration card already displays, NOT the generic vSAN-HCI-SM/MED/LRG
 * reference spec from /readynode-profiles (that one only drives grid ranking/threshold-filtering).
 *
 * Every field is per ESXi HOST, and `totalCoresPerHost` is deliberately named for the trap it
 * exists to avoid: the response's `serverConfiguration.cores.value` is the total across all
 * sockets (proven by every caller multiplying it by host count for a cluster total, and by
 * buildReadOnlyRawThreshold comparing it against a node's `cpuSockets * cores`), whereas the
 * customization panel's `cores` form control - and the sizing request's `components.cpu.cores` -
 * are PER SOCKET. Seeding one from the other without dividing silently multiplies the CPU spec by
 * the socket count.
 *
 * Sockets and clock speed are advisory, not real requirements: for a tier candidate they are
 * synthetic modelling constants (`globalConstants.globalCpuSockets` is always 1,
 * `globalConstants.esaClockSpeed` always 2.6 for ESA). Only the TOTALS (cores, memory, capacity)
 * are genuine per-tier minimums. Consumers derive a real socket count from the selected ReadyNode's
 * own hardware instead; clock speed is still seeded from this spec when present (see
 * `clockSpeedGhz` below) since it's the same number the Hardware Configuration card shows.
 */
export interface RecommendedHardwareSpec {
  /** Total cores across ALL sockets, per host - never per socket. */
  totalCoresPerHost?: number;
  clockSpeedGhz?: number;
  memoryGb?: number;
  drivesPerHost?: number;
  driveSizeTb?: number;
  /** Target raw capacity per host in TB - `driveSizeTb * drivesPerHost` when both are known. */
  rawCapacityTbPerHost?: number;
  networkGbe?: number;
  networkGbeNS?: number;
}

/**
 * `StrValueUnit` declares BOTH `unit` and `units` (backends populate one or the other, inconsistently)
 * - reading only one of them silently mistakes a GB value for a TB value.
 */
function unitOf(spec: { unit?: string; units?: string } | undefined): string {
  return (spec?.units ?? spec?.unit ?? '').toString().toUpperCase();
}

function numberOf(value: string | number | undefined | null): number | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const parsed = parseFloat(value.toString());
  return isNaN(parsed) ? undefined : parsed;
}

/** GB -> TB when the payload says GB; anything else (TB, or an absent unit) is taken as already TB. */
function toTb(spec: PcsSizingEngineResponse.StrValueUnit | undefined): number | undefined {
  const value = numberOf(spec?.value);
  return value === undefined ? undefined : unitOf(spec) === 'GB' ? value / 1000 : value;
}

/** TB -> GB when the payload says TB; anything else (GB, or an absent unit) is taken as already GB. */
function toGb(spec: PcsSizingEngineResponse.StrValueUnit | undefined): number | undefined {
  const value = numberOf(spec?.value);
  return value === undefined ? undefined : unitOf(spec) === 'TB' ? value * 1000 : value;
}

/** diskGroupConfiguration/diskConfiguration are two parallel fields for the same data - some
 *  cluster shapes (e.g. OSA tier-candidate clusters) only populate the latter. */
function capacitySpecOf(cluster: PcsSizingEngineResponse.Cluster | undefined) {
  return cluster?.diskGroupConfiguration?.capacitySpecification ?? cluster?.diskConfiguration?.capacitySpecification;
}

/**
 * Per-host capacity drive count. `serverConfiguration.capacityDrive.value` is authoritative;
 * driveDistribution is OSA-specific and can be empty/all-zero (e.g. for ESA clusters), which would
 * otherwise report 0 whenever a present-but-empty capacitySpecification existed. `undefined` rather
 * than 0 when neither is usable, so callers can tell "no data" from "no drives".
 */
export function getDrivesPerHost(cluster: PcsSizingEngineResponse.Cluster | undefined): number | undefined {
  const fromServer = cluster?.serverConfiguration?.capacityDrive?.value;
  // != null, not truthiness: a legitimate 0 is real data and must win over driveDistribution.
  if (fromServer != null) {
    return fromServer;
  }
  const driveDistribution = capacitySpecOf(cluster)?.driveDistribution;
  const summed = driveDistribution?.reduce((total, entry) => total + (entry?.numberOfDrives || 0), 0);
  return summed || undefined;
}

/** Per-host capacity drive size in TB - `undefined` when the cluster carries no drive-size data. */
export function getDriveSizeTb(cluster: PcsSizingEngineResponse.Cluster | undefined): number | undefined {
  return toTb(capacitySpecOf(cluster)?.selectedCapacityDriveSize);
}

/**
 * Extracts one cluster's per-host spec. Returns `undefined` only when there is no
 * serverConfiguration at all; individual fields stay `undefined` when their source data is absent,
 * so consumers can fall back per field rather than treating a partial response as a zeroed spec.
 */
export function getRecommendedHardwareSpecFromCluster(
  cluster: PcsSizingEngineResponse.Cluster | undefined,
): RecommendedHardwareSpec | undefined {
  const server = cluster?.serverConfiguration;
  if (!server) {
    return undefined;
  }

  const cpu = server.chassis?.components?.cpu?.available?.[0];
  const storage = server.chassis?.components?.storage?.available?.[0];
  const drivesPerHost = numberOf(storage?.maxDriveSlots?.value) ?? getDrivesPerHost(cluster);
  const driveSizeTb = toTb(storage?.capacityDriveSize) ?? getDriveSizeTb(cluster);
  // The backend derives a node's profile eligibility from `capacityDriveSize * maxDriveSlots`
  // (ClusterDefinitionReservationServiceImpl#buildHardwareRequirement), so the target this spec
  // must preserve is that same product - not serverConfiguration.rawCapacity, which carries
  // overheads (hence the parallel rawCapacityNoOverheads field) and is only a last-resort
  // fallback for deriving a drive size when the disk spec itself is missing.
  const rawCapacityTbPerHost = driveSizeTb !== undefined && drivesPerHost !== undefined
    ? driveSizeTb * drivesPerHost
    : toTb(server.rawCapacity);

  return {
    // server.cores is already the total across sockets; chassis cores are per socket.
    totalCoresPerHost: numberOf(server.cores?.value)
      ?? (numberOf(cpu?.cores?.value) !== undefined
        ? numberOf(cpu?.cores?.value)! * (numberOf(cpu?.sockets?.value) ?? 1) : undefined),
    // server.part?.clockSpeed does not exist on the wire (confirmed live - the CPU-parts object
    // only ever carries vendor/generation/series/category/model/cost). The Hardware Configuration
    // card's own correct first-rung fallback reads this same chassis field - mirrored here so the
    // Customize panel's seed matches what the card actually displays.
    clockSpeedGhz: numberOf(server.chassis?.components?.cpu?.available?.[0]?.speed?.value),
    memoryGb: toGb(server.memory) ?? toGb(server.chassis?.components?.memory?.available?.[0]?.serverRam),
    drivesPerHost,
    driveSizeTb,
    rawCapacityTbPerHost,
    networkGbe: numberOf(
      cpu?.networkSpeedEW?.value ?? cpu?.networkSpeed?.value,
    ),
    ...(numberOf(cpu?.networkSpeedNS?.value) !== undefined ? { networkGbeNS: numberOf(cpu?.networkSpeedNS?.value) } : {}),
  };
}

/**
 * Traffic-separated network speeds of one response cluster, read from its chassis CPU entry. Only a
 * SON cluster sized with network traffic separation carries `networkSpeedEW`/`networkSpeedNS` (the
 * backend returns them even when the request was identity-only, i.e. No Preference), in which case
 * the single `networkSpeed` is null. Returns undefined unless BOTH are present, so every other
 * cluster/response keeps rendering its single `networkSpeed` exactly as before.
 */
export function getSplitNetworkSpeeds(
  cluster: PcsSizingEngineResponse.Cluster | undefined,
): { ew: string; ns: string } | undefined {
  const cpu = cluster?.serverConfiguration?.chassis?.components?.cpu?.available?.[0];
  const ew = cpu?.networkSpeedEW;
  const ns = cpu?.networkSpeedNS;
  if (numberOf(ew?.value) === undefined || numberOf(ns?.value) === undefined) {
    return undefined;
  }
  return { ew: `${ew!.value} ${ew!.units ?? ''}`.trim(), ns: `${ns!.value} ${ns!.units ?? ''}`.trim() };
}
